import { useState } from 'react';
import { X } from 'lucide-react';
import { useMarketStore } from '@/stores/marketStore';
import { useAuthStore } from '@/stores/authStore';
import { OrderEntryForm } from './GameDetailPanel';
import { futuresBookIdentity, futuresBookTradable, type FuturesSelection } from './futuresDetail';
import type { OrderEntryTarget } from './orderEntryTarget';
import type { MarketSide, VenueBook } from './types';

const noAction = () => {};
const noLegs: [] = [];

/** The shared order form, without an invented GameRow or hotkey target. */
export function FuturesDetailPanel({ selection, onClose }: { selection: FuturesSelection; onClose: () => void }) {
  useMarketStore((s) => s.version);
  const scope = useAuthStore((s) => `${s.user?.id ?? ''}:${s.user?.orgId ?? ''}`);
  const store = useMarketStore.getState();
  const book = store.getFutureBook(selection.sport, selection.venue, selection.ticker);
  const title = store.getMarket(selection.ticker)?.title || selection.ticker;
  const identity = book ? futuresBookIdentity(book, selection.venue) : 'missing';
  const selectionVersion = store.getFutureSelectionVersion(selection.ticker);
  return (
    <aside aria-label="Futures detail" className="bg-card/95 border-border h-full w-96 min-w-0 shrink-0 overflow-y-auto border-l">
      <header className="border-border flex items-start gap-2 border-b p-3">
        <div className="min-w-0 flex-1">
          <div className="text-muted-foreground mb-1 text-xs">{selection.sport.toUpperCase()} · {selection.venue === 'polymarket' ? 'Polymarket' : 'Kalshi'} · Futures</div>
          <h2 className="break-words text-sm font-semibold [overflow-wrap:anywhere]">{title}</h2>
          <p className="text-muted-foreground mt-1 break-all font-mono text-[10px]">{selection.ticker}</p>
        </div>
        <button type="button" aria-label="Close futures detail" onClick={onClose} className="text-muted-foreground shrink-0 rounded p-1 hover:bg-accent"><X size={16} /></button>
      </header>
      {book && futuresBookTradable(book, selection.venue) ? (
        <FuturesTicket key={`${scope}:${selectionVersion}:${identity}`} selection={selection} book={book} title={title} identity={identity} scope={scope} selectionVersion={selectionVersion} />
      ) : <p role="status" className="p-3 text-sm">This market is unpriced, unavailable or its trading details are incomplete. No order ticket is active.</p>}
    </aside>
  );
}

function FuturesTicket({ selection, book, title, identity, scope, selectionVersion }: { selection: FuturesSelection; book: VenueBook; title: string; identity: string; scope: string; selectionVersion: number | null }) {
  const [side, setSide] = useState<MarketSide>('yes');
  const [price, setPrice] = useState('');
  const [orderType, setOrderType] = useState<'limit' | 'market'>('limit');
  const target: OrderEntryTarget = {
    key: `future:${selection.venue}:${selection.ticker}`,
    sport: selection.sport,
    market: { venue: selection.venue, title, book },
    validateCurrent: () => {
      const user = useAuthStore.getState().user;
      if (!user || `${user.id}:${user.orgId ?? ''}` !== scope) return false;
      if (useMarketStore.getState().getFutureSelectionVersion(selection.ticker) !== selectionVersion) return false;
      const current = useMarketStore.getState().getFutureBook(selection.sport, selection.venue, selection.ticker);
      return futuresBookTradable(current, selection.venue)
        && !!current && futuresBookIdentity(current, selection.venue) === identity;
    },
  };
  return <div className="p-3">
    <p className="text-muted-foreground mb-3 text-xs">YES means the named outcome happens; NO means it does not. Snapshot prices can change before submission. Review your limit price and expiry.</p>
    <OrderEntryForm game={target} selectedSide="away" venue={selection.venue}
      orderSide={side} onOrderSideChange={setSide} price={price} onPriceChange={setPrice}
      priceFlashKey={0} orderType={orderType} onOrderTypeChange={setOrderType}
      extraLegs={noLegs} onAddLeg={noAction} onUpdateLeg={noAction} onRemoveLeg={noAction}
      onClearLegs={noAction} oscarReleaseIntent={null} onClearOscarReleaseIntent={noAction} />
  </div>;
}
